import React, { useId } from 'react'
import { getCountries } from 'react-phone-number-input'

import type { BillingProfile } from '@/src/types/billingEngine'

const INPUT = 'tw-mt-1 tw-block tw-w-full tw-rounded-[2px] tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-ink focus:tw-border-emerald-500'
const EU = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '))
const regions = new Intl.DisplayNames(['en'], { type: 'region' })
const countries = getCountries()
  .map((code) => ({ code, name: regions.of(code) || code }))
  .sort((a, b) => a.name.localeCompare(b.name))

export function taxIdLabel(type: string) {
  if (type === 'eu_vat') return 'EU VAT'
  if (type === 'eu_oss_vat') return 'EU OSS VAT'
  const [country, ...name] = type.split('_')
  return `${regions.of(country.toUpperCase()) || country.toUpperCase()} · ${name.join(' ').toUpperCase()}`
}

export function billingDetailsComplete(profile: BillingProfile) {
  return Boolean(profile.name.trim() && profile.email.trim() && profile.address.line1?.trim() && profile.address.city?.trim() && profile.address.country)
}

export function BillingDetailsSummary({ profile }: { profile: BillingProfile }) {
  return (
    <div className='tw-space-y-1 tw-text-sm tw-text-gray-600'>
      <p className='tw-m-0 tw-font-medium tw-text-ink'>{profile.name}</p>
      <p className='tw-m-0'>{profile.email}</p>
      <p className='tw-m-0'>
        {[profile.address.line1, profile.address.line2, profile.address.city, profile.address.state, profile.address.postal_code, profile.address.country && regions.of(profile.address.country)]
          .filter(Boolean)
          .join(', ')}
      </p>
      {profile.tax_ids.map((taxId) => (
        <p className='tw-m-0' key={`${taxId.type}-${taxId.value}`}>
          {taxIdLabel(taxId.type)}: {taxId.value}
          <span className='tw-ml-2 tw-text-xs tw-text-gray-500'>
            {taxId.verification === 'verified'
              ? 'Verified'
              : taxId.verification === 'pending'
                ? 'Verification pending'
                : taxId.verification === 'unverified'
                  ? 'Not verified'
                  : 'Verification unavailable'}
          </span>
        </p>
      ))}
    </div>
  )
}

export function BillingDetailsFields({
  value,
  onChange,
  taxIdTypes,
  disabled = false,
}: {
  value: BillingProfile
  onChange: (profile: BillingProfile) => void
  taxIdTypes: string[]
  disabled?: boolean
}) {
  const id = useId()
  const country = value.address.country || ''
  const availableTypes = taxIdTypes.filter((type) => type.startsWith(`${country.toLowerCase()}_`) || (type.startsWith('eu_') && EU.has(country)))
  const addressField = (field: keyof BillingProfile['address'], label: string, required = false) => (
    <label className='tw-block tw-text-sm tw-text-gray-700' htmlFor={`${id}-${field}`}>
      {label}
      <input
        id={`${id}-${field}`}
        className={INPUT}
        value={value.address[field] || ''}
        maxLength={254}
        required={required}
        onChange={(event) => onChange({ ...value, address: { ...value.address, [field]: event.target.value } })}
      />
    </label>
  )

  return (
    <fieldset disabled={disabled} className='tw-m-0 tw-min-w-0 tw-space-y-3 tw-border-0 tw-p-0'>
      <label className='tw-block tw-text-sm tw-text-gray-700' htmlFor={`${id}-name`}>
        Legal business or billing name
        <input id={`${id}-name`} className={INPUT} value={value.name} maxLength={254} required onChange={(event) => onChange({ ...value, name: event.target.value })} autoComplete='organization' />
      </label>
      <label className='tw-block tw-text-sm tw-text-gray-700' htmlFor={`${id}-email`}>
        Billing email
        <input
          id={`${id}-email`}
          className={INPUT}
          value={value.email}
          maxLength={254}
          required
          type='email'
          onChange={(event) => onChange({ ...value, email: event.target.value })}
          autoComplete='email'
        />
      </label>
      <label className='tw-block tw-text-sm tw-text-gray-700' htmlFor={`${id}-country`}>
        Country
        <select id={`${id}-country`} className={INPUT} value={country} required onChange={(event) => onChange({ ...value, address: { ...value.address, country: event.target.value } })}>
          <option value=''>Select country</option>
          {countries.map(({ code, name }) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </label>
      {addressField('line1', 'Street address', true)}
      {addressField('line2', 'Address line 2 (optional)')}
      <div className='tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2'>
        {addressField('city', 'City', true)}
        {addressField('postal_code', 'Postal code')}
      </div>
      {addressField('state', 'State / province / region')}
      <div className='tw-space-y-3'>
        <p className='tw-m-0 tw-text-sm tw-font-medium tw-text-gray-700'>Business tax IDs (optional)</p>
        {value.tax_ids.map((taxId, index) => (
          <div key={index} className='tw-space-y-2'>
            <label className='tw-block tw-text-sm tw-text-gray-700'>
              Tax ID type
              <select
                className={INPUT}
                aria-label={`Tax ID ${index + 1} type`}
                value={taxId.type}
                onChange={(event) => onChange({ ...value, tax_ids: value.tax_ids.map((item, i) => (i === index ? { type: event.target.value, value: item.value } : item)) })}
              >
                {[...new Set([taxId.type, ...availableTypes])].map((type) => (
                  <option key={type} value={type}>
                    {taxIdLabel(type)}
                  </option>
                ))}
              </select>
            </label>
            <div className='tw-flex tw-items-end tw-gap-3'>
              <label className='tw-min-w-0 tw-flex-1 tw-text-sm tw-text-gray-700'>
                Tax ID number
                <input
                  className={INPUT}
                  aria-label={`Tax ID ${index + 1} number`}
                  value={taxId.value}
                  required
                  maxLength={100}
                  onChange={(event) => onChange({ ...value, tax_ids: value.tax_ids.map((item, i) => (i === index ? { type: item.type, value: event.target.value } : item)) })}
                />
              </label>
              <button
                type='button'
                className='tw-border-0 tw-bg-transparent tw-py-2 tw-text-sm tw-text-red-600'
                aria-label={`Remove tax ID ${index + 1}`}
                onClick={() => onChange({ ...value, tax_ids: value.tax_ids.filter((_, i) => i !== index) })}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        {availableTypes.length > 0 && value.tax_ids.length < 5 && (
          <button
            type='button'
            className='tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-brand-strong'
            onClick={() => onChange({ ...value, tax_ids: [...value.tax_ids, { type: availableTypes[0], value: '' }] })}
          >
            + Add tax ID
          </button>
        )}
      </div>
    </fieldset>
  )
}
